import type { ImageAssetWithAltText } from '@fhs/ui';

import type { Step } from '../components/guide/types';
import type { SelectedPicker } from '../state/combo-selection';
import type { EditComboItem } from '../types';

export const willAddAsCombo = ({
  hasCombo,
  isComboEnabled,
  isComboRequired,
}: {
  hasCombo: boolean;
  isComboEnabled: boolean;
  isComboRequired: boolean;
}) => hasCombo && (isComboEnabled || isComboRequired);

export const getComboSelectedItemIds = (
  selectedPickers: Record<string, SelectedPicker>
): string[] =>
  Object.values(selectedPickers)
    .map(picker => picker?.id)
    .filter((id): id is string => Boolean(id));

export const buildDefaultComboSelectedPickers = (
  guideSteps: Step[],
  editComboItems?: EditComboItem[]
): Record<string, SelectedPicker> => {
  const selectedPickers: Record<string, SelectedPicker> = {};

  for (const step of guideSteps) {
    const editComboItem = editComboItems?.find(item => item.slotId === step.id);
    const itemIdToSelect = editComboItem?.itemId ?? step.preSelectedItemId;

    if (!itemIdToSelect) {
      continue;
    }

    const itemToPreSelect = step.sections[0]?.items.find(item => item.id === itemIdToSelect);

    if (!itemToPreSelect) {
      continue;
    }

    const basePriceCents = itemToPreSelect.priceCents ?? 0;
    const modifiersPriceCents = (editComboItem?.selectedCustomizationOptions ?? []).reduce(
      (cents, option) => cents + option.upChargeCents,
      0
    );

    selectedPickers[step.id] = {
      id: itemToPreSelect.id,
      slug: itemToPreSelect.slug,
      displayName: itemToPreSelect.displayName,
      priceCents: basePriceCents + modifiersPriceCents,
      basePriceCents,
      requiresCustomization: itemToPreSelect.requiresCustomization ?? false,
      image: itemToPreSelect.image as ImageAssetWithAltText,
      baseCalories: itemToPreSelect.baseCalories ?? undefined,
      userCustomizationSelections: editComboItem?.customizationSelections,
      resolvedCustomizationSelections: editComboItem?.customizationSelections,
      selectedCustomizationOptions: editComboItem?.selectedCustomizationOptions,
    };
  }

  return selectedPickers;
};

export const getRequiredComboSlotIds = ({
  willAddAsCombo: addingAsCombo,
  isComboRequired,
  guideSteps,
  comboSlotCount,
}: {
  willAddAsCombo: boolean;
  isComboRequired: boolean;
  guideSteps: Step[];
  comboSlotCount: number;
}): string[] => {
  if (!addingAsCombo) {
    return [];
  }

  if (isComboRequired) {
    // `combo.slots` can be missing/empty in the menu picker response even when the guide has
    // steps (common on slower deployed environments). Fall back to all guide steps so we don't
    // treat "zero slots configured" as "nothing to wait for".
    const slotsToFill = comboSlotCount > 0 ? comboSlotCount : guideSteps.length;
    return guideSteps.slice(0, slotsToFill).map(step => step.id);
  }

  return guideSteps.map(step => step.id);
};

export const isComboAddToCartReady = ({
  willAddAsCombo: addingAsCombo,
  guideSteps,
  selectedPickers,
  isComboRequired,
  comboSlotCount,
}: {
  willAddAsCombo: boolean;
  guideSteps: Step[];
  selectedPickers: Record<string, SelectedPicker>;
  isComboRequired: boolean;
  comboSlotCount: number;
}): boolean => {
  if (!addingAsCombo) {
    return true;
  }

  if (!guideSteps.length) {
    return false;
  }

  // Backend invariant: `isCombo: true` requires at least one combo item id in `comboItemsJson`.
  if (getComboSelectedItemIds(selectedPickers).length === 0) {
    return false;
  }

  const requiredSlotIds = getRequiredComboSlotIds({
    willAddAsCombo: addingAsCombo,
    isComboRequired,
    guideSteps,
    comboSlotCount,
  });

  if (requiredSlotIds.length === 0) {
    return getComboSelectedItemIds(selectedPickers).length > 0;
  }

  return requiredSlotIds.every(slotId => Boolean(selectedPickers[slotId]?.id));
};
